import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface PlatformSetting {
  value: any;
  category: string;
  description: string;
  updatedAt: string;
}

export interface PlatformSettings {
  // AI Configuration
  default_ai_provider: string;
  default_ai_model: string;
  ai_rate_limit_per_minute: number;
  ai_enabled: boolean;
  
  // Feature Toggles
  feature_web_research: boolean;
  feature_autopilot_mode: boolean;
  feature_rag_system: boolean;
  feature_file_uploads: boolean;
  feature_export_pdf: boolean;
  
  // Access Control
  registration_enabled: boolean;
  maintenance_mode: boolean;
  maintenance_message: string;
  
  // Limits
  max_projects_per_user: number;
  max_research_items_per_project: number;
  max_file_upload_mb: number;
  
  // Branding
  platform_name: string;
  support_email: string;
}

const DEFAULT_SETTINGS: PlatformSettings = {
  default_ai_provider: 'platform',
  default_ai_model: 'google/gemini-2.5-flash',
  ai_rate_limit_per_minute: 20,
  ai_enabled: true,
  feature_web_research: true,
  feature_autopilot_mode: true,
  feature_rag_system: true,
  feature_file_uploads: true,
  feature_export_pdf: true,
  registration_enabled: true,
  maintenance_mode: false,
  maintenance_message: 'We are currently performing maintenance. Please check back soon.',
  max_projects_per_user: 50,
  max_research_items_per_project: 500,
  max_file_upload_mb: 25,
  platform_name: 'AtlasDraft',
  support_email: 'support@atlasdraft.app',
};

interface UsePlatformSettingsReturn {
  settings: PlatformSettings;
  rawSettings: Record<string, PlatformSetting>;
  isLoading: boolean;
  error: string | null;
  getSetting: <K extends keyof PlatformSettings>(key: K) => PlatformSettings[K];
  updateSetting: (key: string, value: any) => Promise<boolean>;
  updateSettings: (updates: Array<{ key: string; value: any }>) => Promise<boolean>;
  refetch: () => Promise<void>;
}

async function fetchPlatformSettings(): Promise<Record<string, PlatformSetting>> {
  const { data, error } = await supabase.functions.invoke('get-platform-settings');
  if (error) throw new Error(error.message);
  return data.settings || {};
}

export function usePlatformSettings(): UsePlatformSettingsReturn {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: rawSettings = {}, isLoading, error: queryError } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: fetchPlatformSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Parse settings into typed object
  const settings: PlatformSettings = {
    ...DEFAULT_SETTINGS,
    ...Object.entries(rawSettings).reduce<Record<string, any>>((acc, [key, setting]) => {
      acc[key] = setting.value;
      return acc;
    }, {}),
  } as PlatformSettings;

  const getSetting = <K extends keyof PlatformSettings>(key: K): PlatformSettings[K] => {
    return settings[key];
  };

  const updateSettingMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      const { error } = await supabase.functions.invoke('update-platform-setting', {
        body: { key, value },
      });
      if (error) throw new Error(error.message);
      return { key, value };
    },
    onSuccess: ({ key, value }) => {
      queryClient.setQueryData(['platform-settings'], (old: Record<string, PlatformSetting> | undefined) => ({
        ...old,
        [key]: {
          ...old?.[key],
          value,
          updatedAt: new Date().toISOString(),
        },
      }));
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (updates: Array<{ key: string; value: any }>) => {
      const { error } = await supabase.functions.invoke('update-platform-setting', {
        body: { updates },
      });
      if (error) throw new Error(error.message);
      return updates;
    },
    onSuccess: (updates) => {
      queryClient.setQueryData(['platform-settings'], (old: Record<string, PlatformSetting> | undefined) => {
        const newSettings = { ...old };
        for (const update of updates) {
          newSettings[update.key] = {
            ...newSettings[update.key],
            value: update.value,
            updatedAt: new Date().toISOString(),
          };
        }
        return newSettings;
      });
    },
  });

  const updateSetting = async (key: string, value: any): Promise<boolean> => {
    try {
      await updateSettingMutation.mutateAsync({ key, value });
      return true;
    } catch (err) {
      console.error('Error updating platform setting:', err);
      return false;
    }
  };

  const updateSettings = async (updates: Array<{ key: string; value: any }>): Promise<boolean> => {
    try {
      await updateSettingsMutation.mutateAsync(updates);
      return true;
    } catch (err) {
      console.error('Error updating platform settings:', err);
      return false;
    }
  };

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
  }, [queryClient]);

  return {
    settings,
    rawSettings,
    isLoading: isLoading && Object.keys(rawSettings).length === 0,
    error: queryError?.message || null,
    getSetting,
    updateSetting,
    updateSettings,
    refetch,
  };
}
